import BlockWrapper from "blocks/components/BlockWrapper";
import { usePageContext } from "lib/context/page-context";
import { useTheme } from "lib/theme";
import { ConnectedEditorKanban } from "./components/ConnectedEditorKanban";
import { ConnectedLiveKanban } from "./components/ConnectedLiveKanban";
import type { KanbanBlock as KanbanBlockType } from "../types";
import type { BlockProps } from "blocks/v2/__shared__/types";
import { Suspense } from "react";
import KanbanSkeleton from "./components/Skeleton";
import { ErrorBoundary } from "react-error-boundary";
import { InformativeBlockErrorState } from "blocks/components/BlockErrors/InformativeBlockErrorState";
import { TRPCClientError } from "@trpc/client";

export const KanbanBlock = (props: BlockProps<KanbanBlockType>) => {
  const { isEditing } = usePageContext();
  const { stateId, config } = props.block;
  const theme = useTheme();

  const ConnectedKanban = isEditing
    ? ConnectedEditorKanban
    : ConnectedLiveKanban;

  if (!isEditing && (!stateId || !config.tableId)) return null;

  return (
    <BlockWrapper
      blockId={props.blockId}
      block={props.block}
      maxWidth={
        config.style?.width
          ? theme.app.width[config.style?.width]
          : theme.app.width.full
      }
      alignment={config.style?.alignment}
    >
      <ErrorBoundary
        fallbackRender={({ error }) => {
          // If we're in the editor and the error is a permissions error, show a
          // special error state to help the builder fix the issue. Otherwise,
          // rethrow the error to let the standard block error state render.
          if (
            isEditing &&
            error instanceof TRPCClientError &&
            error.data.code === "tables:permissions:errors"
          ) {
            return (
              <InformativeBlockErrorState
                title="Data not available"
                description="This component is connected to a Table you don't have access to."
                iconBefore="navTable"
                iconAfter="formAdd"
              />
            );
          } else {
            throw error;
          }
        }}
      >
        <Suspense
          fallback={
            <KanbanSkeleton block={props.block} blockId={props.blockId} />
          }
        >
          <ConnectedKanban block={props.block} blockId={props.blockId} />
        </Suspense>
      </ErrorBoundary>
    </BlockWrapper>
  );
};
